import { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  LogOut,
  Search,
  Trash2,
  Download,
  Mail,
  Users,
  Briefcase,
  MessageSquare,
  Inbox,
  RefreshCw,
  AlertCircle,
  Check,
  Archive,
  BarChart3,
  Calendar as CalendarIcon,
} from "lucide-react";
import { adminApi, useAdminAuth, loginWithGoogle } from "@/lib/adminAuth";
import StudioBackdrop from "@/components/podcast/StudioBackdrop";
import Analytics from "@/components/admin/Analytics";
import Schedule from "@/components/admin/Schedule";

const TABS = [
  { key: "analytics", label: "Analytics", icon: BarChart3, color: "#1E6FE0" },
  { key: "schedule", label: "Schedule", icon: CalendarIcon, color: "#C9A24A" },
  { key: "guest-applications", label: "Guest Apps", icon: Users, color: "#E10600" },
  { key: "contact-inquiries", label: "Contact", icon: Inbox, color: "#1E6FE0" },
  { key: "sponsorship-inquiries", label: "Sponsorship", icon: Briefcase, color: "#E10600" },
  { key: "newsletter-signups", label: "Newsletter", icon: Mail, color: "#1E6FE0" },
];

export default function Admin() {
  const { user, checked, logout } = useAdminAuth();
  const location = useLocation();
  const [authError, setAuthError] = useState(location.state?.authError || null);

  useEffect(() => {
    if (location.state?.authError) {
      window.history.replaceState({}, "", "/admin");
    }
  }, [location.state]);

  if (!checked) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-white">
        <p className="font-display text-3xl uppercase">Loading…</p>
      </div>
    );
  }

  if (!user) {
    return <Login authError={authError} onClear={() => setAuthError(null)} />;
  }

  return <Dashboard user={user} onLogout={logout} />;
}

function Login({ authError, onClear }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] relative flex items-center justify-center px-4 film-grain overflow-hidden">
      <StudioBackdrop opacity={0.14} size={260} />
      <div
        className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full opacity-30 pointer-events-none"
        style={{ background: "radial-gradient(circle, #E10600 0%, transparent 60%)" }}
      />
      <div
        className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full opacity-30 pointer-events-none"
        style={{ background: "radial-gradient(circle, #1E6FE0 0%, transparent 60%)" }}
      />

      <div className="relative max-w-md w-full">
        <div className="border border-[#262626] bg-[#0F0F0F] p-8 md:p-12">
          <p className="text-[10px] tracking-[0.3em] uppercase font-bold gold-shimmer mb-4">
            Restricted
          </p>
          <h1 className="font-display text-5xl md:text-6xl uppercase leading-none">
            Admin <span className="text-[#E10600]">Console</span>
          </h1>
          <p className="mt-4 text-zinc-400">
            Sign in with the show's Google account to view submissions and manage the
            inbox.
          </p>

          {authError && (
            <div
              data-testid="admin-auth-error"
              className="mt-6 border border-[#E10600] bg-[#E10600]/10 p-4 flex items-start gap-3"
            >
              <AlertCircle className="w-5 h-5 text-[#E10600] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-white">{authError}</p>
                <button
                  onClick={onClear}
                  className="mt-2 text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-400 hover:text-white"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          <button
            data-testid="admin-google-login"
            onClick={loginWithGoogle}
            className="btn-primary w-full justify-center mt-8 light-sweep"
          >
            Continue With Google
          </button>

          <p className="mt-4 text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-500 text-center">
            Only theproveyourpointpodcast@gmail.com
          </p>
        </div>
      </div>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState(TABS[0].key);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState({});
  const [error, setError] = useState({});
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async (tab) => {
    if (tab === "analytics" || tab === "schedule") return;
    setLoading((s) => ({ ...s, [tab]: true }));
    setError((s) => ({ ...s, [tab]: null }));
    try {
      const r = await adminApi.get(`/${tab}`);
      setData((s) => ({ ...s, [tab]: r.data }));
    } catch (e) {
      setError((s) => ({ ...s, [tab]: e?.response?.data?.detail || "Load failed" }));
    } finally {
      setLoading((s) => ({ ...s, [tab]: false }));
    }
  };

  useEffect(() => {
    if (activeTab !== "analytics" && activeTab !== "schedule" && !data[activeTab]) load(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const items = useMemo(() => {
    const arr = data[activeTab] || [];
    let out = arr;
    if (statusFilter !== "all") {
      out = out.filter((i) => (i.status || "new") === statusFilter);
    }
    if (query) {
      const q = query.toLowerCase();
      out = out.filter((i) =>
        JSON.stringify(i).toLowerCase().includes(q)
      );
    }
    return out;
  }, [data, activeTab, statusFilter, query]);

  const tabCounts = useMemo(() => {
    const out = {};
    for (const t of TABS) {
      const arr = data[t.key] || [];
      out[t.key] = arr.filter((i) => (i.status || "new") === "new").length;
    }
    return out;
  }, [data]);

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this entry? This can't be undone.")) return;
    try {
      await adminApi.delete(`/admin/${activeTab}/${id}`);
      load(activeTab);
    } catch {
      alert("Delete failed");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await adminApi.patch(`/admin/${activeTab}/${id}/status`, { status });
      load(activeTab);
    } catch {
      alert("Status update failed");
    }
  };

  const exportCsv = () => {
    const arr = items;
    if (arr.length === 0) return;
    const headers = Array.from(
      new Set(arr.flatMap((o) => Object.keys(o)))
    );
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const csv = [
      headers.join(","),
      ...arr.map((row) => headers.map((h) => escape(row[h])).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pyp-${activeTab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0A] text-white film-grain">
      {/* Top bar */}
      <header className="border-b border-[#262626] bg-[#0A0A0A]/95 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 bg-[#E10600] flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-white" />
            </span>
            <p className="font-display text-xl uppercase tracking-tight">
              PYP · <span className="text-[#E10600]">Admin</span>
            </p>
          </div>
          <div className="flex items-center gap-4">
            {user.picture && (
              <img
                src={user.picture}
                alt=""
                className="w-8 h-8 rounded-full border border-[#262626]"
              />
            )}
            <div className="hidden sm:block text-right">
              <p className="text-xs text-white font-bold">{user.name || "Admin"}</p>
              <p className="text-[10px] tracking-widest font-mono text-zinc-500">{user.email}</p>
            </div>
            <button
              data-testid="admin-logout"
              onClick={onLogout}
              className="flex items-center gap-2 px-3 py-2 border border-[#262626] hover:border-[#E10600] text-xs tracking-[0.15em] uppercase font-bold transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Stats / tabs */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-px bg-[#262626] border border-[#262626] mb-8">
          {TABS.map((t) => {
            const isSpecial = t.key === "analytics" || t.key === "schedule";
            const total = isSpecial ? null : (data[t.key] || []).length;
            const newCount = isSpecial ? 0 : (tabCounts[t.key] || 0);
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`admin-tab-${t.key}`}
                onClick={() => setActiveTab(t.key)}
                className={`p-5 text-left transition-colors ${
                  active ? "bg-[#121212]" : "bg-[#0A0A0A] hover:bg-[#121212]"
                }`}
              >
                <div className="flex items-center justify-between">
                  <t.icon className="w-5 h-5" style={{ color: t.color }} />
                  {newCount > 0 && (
                    <span
                      className="text-[10px] font-bold px-2 py-0.5 text-white"
                      style={{ background: t.color }}
                    >
                      {newCount} NEW
                    </span>
                  )}
                </div>
                <p
                  className="mt-3 font-display text-3xl uppercase leading-none"
                  style={{ color: active ? t.color : "#FFFFFF" }}
                >
                  {isSpecial ? "—" : total}
                </p>
                <p className="mt-2 text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-500">
                  {t.label}
                </p>
              </button>
            );
          })}
        </div>

        {activeTab === "analytics" ? (
          <Analytics />
        ) : activeTab === "schedule" ? (
          <Schedule />
        ) : (
          <>
            {/* Controls */}
            <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between mb-6">
              <div className="flex items-center gap-3 flex-1 max-w-md">
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
                  <input
                    data-testid="admin-search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search…"
                    className="input-edge pl-9"
                  />
                </div>
                {activeTab !== "newsletter-signups" && (
                  <select
                    data-testid="admin-status-filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="bg-[#0A0A0A] border border-[#262626] text-white text-xs tracking-widest uppercase font-bold px-3 py-3"
                  >
                    <option value="all">ALL</option>
                    <option value="new">NEW</option>
                    <option value="read">READ</option>
                    <option value="archived">ARCHIVED</option>
                  </select>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="admin-refresh"
                  onClick={() => load(activeTab)}
                  className="flex items-center gap-2 px-3 py-2 border border-[#262626] hover:border-[#1E6FE0] text-xs tracking-[0.15em] uppercase font-bold transition-colors"
                >
                  <RefreshCw className={`w-4 h-4 ${loading[activeTab] ? "animate-spin" : ""}`} />
                  Refresh
                </button>
                <button
                  data-testid="admin-export-csv"
                  onClick={exportCsv}
                  className="flex items-center gap-2 px-3 py-2 border border-[#262626] hover:border-[#E10600] text-xs tracking-[0.15em] uppercase font-bold transition-colors"
                >
                  <Download className="w-4 h-4" />
                  CSV
                </button>
              </div>
            </div>

            {/* Body */}
            {error[activeTab] && (
              <div className="border border-[#E10600] bg-[#E10600]/10 p-4 mb-6">
                {error[activeTab]}
              </div>
            )}

            {loading[activeTab] && !data[activeTab] ? (
              <p className="text-zinc-500 font-mono">Loading…</p>
            ) : items.length === 0 ? (
              <div className="border border-[#262626] p-16 text-center">
                <p className="text-zinc-500 font-mono text-sm">No entries yet.</p>
              </div>
            ) : (
              <div data-testid="admin-list" className="space-y-px bg-[#262626] border border-[#262626]">
                {items.map((item) => (
                  <EntryCard
                    key={item.id}
                    item={item}
                    tab={activeTab}
                    onDelete={() => deleteItem(item.id)}
                    onStatus={(s) => setStatus(item.id, s)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const s = status || "new";
  const map = {
    new: { bg: "#E10600", color: "#FFFFFF" },
    read: { bg: "#1E6FE0", color: "#FFFFFF" },
    archived: { bg: "#262626", color: "#A1A1AA" },
  };
  const c = map[s] || map.new;
  return (
    <span
      className="px-2 py-1 text-[10px] tracking-[0.2em] uppercase font-bold"
      style={{ background: c.bg, color: c.color }}
    >
      {s}
    </span>
  );
}

function EntryCard({ item, tab, onDelete, onStatus }) {
  const created = item.created_at
    ? new Date(item.created_at).toLocaleString()
    : "";

  return (
    <article
      data-testid="admin-entry"
      className="bg-[#0A0A0A] hover:bg-[#0F0F0F] p-5 md:p-6 transition-colors"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            {tab !== "newsletter-signups" && <StatusPill status={item.status} />}
            <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-500">
              {created}
            </p>
          </div>
          <h3 className="mt-2 font-display text-xl md:text-2xl uppercase leading-tight">
            {summaryTitle(item, tab)}
          </h3>
          <p className="mt-1 text-sm text-zinc-400">
            {item.email}{" "}
            {item.phone && (
              <span className="text-zinc-600">· {item.phone}</span>
            )}
          </p>

          <dl className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            {Object.entries(item)
              .filter(([k]) => !["id", "status", "created_at", "email", "phone"].includes(k))
              .map(([k, v]) => (
                <div key={k} className="flex flex-col">
                  <dt className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-500">
                    {k.replace(/_/g, " ")}
                  </dt>
                  <dd className="text-zinc-200 break-words">{String(v ?? "")}</dd>
                </div>
              ))}
          </dl>
        </div>

        <div className="flex items-center gap-2">
          {tab !== "newsletter-signups" && (
            <>
              {(item.status || "new") !== "read" && (
                <button
                  data-testid="admin-action-read"
                  onClick={() => onStatus("read")}
                  title="Mark as read"
                  className="w-9 h-9 border border-[#262626] hover:border-[#1E6FE0] flex items-center justify-center transition-colors"
                >
                  <Check className="w-4 h-4 text-zinc-400" />
                </button>
              )}
              {(item.status || "new") !== "archived" && (
                <button
                  data-testid="admin-action-archive"
                  onClick={() => onStatus("archived")}
                  title="Archive"
                  className="w-9 h-9 border border-[#262626] hover:border-zinc-500 flex items-center justify-center transition-colors"
                >
                  <Archive className="w-4 h-4 text-zinc-400" />
                </button>
              )}
            </>
          )}
          <a
            href={`mailto:${item.email}`}
            title="Reply via email"
            className="w-9 h-9 border border-[#262626] hover:border-[#E10600] flex items-center justify-center transition-colors"
          >
            <Mail className="w-4 h-4 text-zinc-400" />
          </a>
          <button
            data-testid="admin-action-delete"
            onClick={onDelete}
            title="Delete"
            className="w-9 h-9 border border-[#262626] hover:border-[#E10600] hover:bg-[#E10600]/10 flex items-center justify-center transition-colors"
          >
            <Trash2 className="w-4 h-4 text-zinc-400" />
          </button>
        </div>
      </div>
    </article>
  );
}

function summaryTitle(item, tab) {
  switch (tab) {
    case "guest-applications":
      return item.full_name || "Guest Application";
    case "contact-inquiries":
      return item.subject || "Contact";
    case "sponsorship-inquiries":
      return item.company_name || "Sponsorship";
    case "newsletter-signups":
      return item.email || "Subscriber";
    default:
      return "Entry";
  }
}
